// Taking payment for a finished work order, the three ways a truck shop gets paid:
//   card (Stripe: the customer scans a QR code or gets a link), fleet checks (EFS / Comchek / T-Chek with an express code),
//   and cash. Plus "other" (paper check, ACH, card on the shop's own terminal).
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { FLEET_CHECKS } from '@vegapart/shared';
import { api } from './api';
import { fromCents, money } from './format';
import { useMe } from './session';
import { Modal, toast, toastError } from './ui';
import { Icon } from './icons';
import { uploadFile, fileUrl } from './upload';
import type { WorkOrder } from './types';

type Way = 'card' | 'fleet' | 'cash' | 'other';
type ConnectInfo = { connected: boolean; charges_enabled: boolean };

const cents = (v: string) => Math.round(Number(String(v).replace(/[^0-9.]/g, '')) * 100) || 0;

export function TakePaymentDialog({ wo, onClose, onDone }: { wo: WorkOrder; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [way, setWay] = useState<Way | null>(null);
  const ways: [Way, string, string][] = [
    ['card', 'dollar', t('take.card')], ['fleet', 'receipt', t('take.fleet')], ['cash', 'invoice', t('take.cash')], ['other', 'plus', t('take.other')]
  ];
  return (
    <Modal title={t('take.title', { amount: money(wo.balance_cents) })} onClose={onClose}>
      {!way ? (
        <div className="take-ways">
          {ways.map(([k, icon, label]) => (
            <button key={k} type="button" className="take-way" onClick={() => setWay(k)}>
              <Icon name={icon} size={26} /><b>{label}</b><span>{t('take.' + k + '_hint')}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="stack">
          <button type="button" className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => setWay(null)}>← {t('common.back')}</button>
          {way === 'card' && <CardWay wo={wo} onDone={onDone} />}
          {way === 'fleet' && <FleetWay wo={wo} onDone={onDone} />}
          {way === 'cash' && <CashWay wo={wo} onDone={onDone} />}
          {way === 'other' && <OtherWay wo={wo} onDone={onDone} />}
        </div>
      )}
    </Modal>
  );
}

async function record(woId: number, body: Record<string, unknown>) {
  await api.post(`/work-orders/${woId}/payments`, body);
}

function AmountInput({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return <label className="f">{label} *<input type="number" inputMode="decimal" step="0.01" min="0" value={value} onChange={e => onChange(e.target.value)} required /></label>;
}

// ---------- Card (Stripe) ----------
function CardWay({ wo, onDone }: { wo: WorkOrder; onDone: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const connect = useQuery({ queryKey: ['stripe-connect'], queryFn: () => api.get<ConnectInfo>('/payments/connect').catch(() => null), enabled: me.has('shop') });
  const [url, setUrl] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(false);
  const [amount, setAmount] = useState(fromCents(wo.balance_cents));
  const ready = connect.data?.charges_enabled;

  const makeLink = async (send: boolean) => {
    setBusy(true);
    try {
      const r = await api.post<{ url: string; sent_to: string | null }>(`/work-orders/${wo.id}/payment-link`, { send, ...(email.trim() ? { email: email.trim() } : {}) });
      setUrl(r.url);
      setQr(await QRCode.toDataURL(r.url, { margin: 1, width: 260 }));
      if (send) toast(t('pay.sent', { email: r.sent_to }));
    } catch (e) { toastError(e); } finally { setBusy(false); }
  };
  useEffect(() => { if (ready && !url) void makeLink(false); }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  if (manual) return (
    <form className="stack" onSubmit={async e => {
      e.preventDefault();
      setBusy(true);
      try { await record(wo.id, { amount_cents: cents(amount), method: 'card', note: t('take.terminal') }); toast(t('common.saved')); onDone(); }
      catch (err) { toastError(err); } finally { setBusy(false); }
    }}>
      <p className="sub" style={{ margin: 0 }}>{t('take.terminal_hint')}</p>
      <AmountInput value={amount} onChange={setAmount} label={t('pay.amount')} />
      <button className="btn primary" disabled={busy}>{t('take.save')}</button>
    </form>
  );

  return (
    <div className="stack">
      {connect.data === undefined && me.has('shop') ? <div className="sub">…</div> : !ready ? (
        <div className="note warn">{t('pay.connect_first')} {me.isAdmin && <Link to="/settings#payments">{t('nav.settings')} →</Link>}</div>
      ) : (
        <>
          <p className="sub" style={{ margin: 0 }}>{t('take.card_scan')}</p>
          <div className="take-qr">{qr ? <img src={qr} alt={t('take.qr_alt')} width={260} height={260} /> : <div className="sub">…</div>}</div>
          {url && <div className="copy-row"><input readOnly value={url} onFocus={e => e.target.select()} />
            <button type="button" className="btn sm" onClick={() => { void navigator.clipboard?.writeText(url); toast(t('pay.copied')); }}>{t('pay.copy')}</button></div>}
          <label className="f">{t('pay.email')}<input type="email" value={email} placeholder={t('pay.email_default')} onChange={e => setEmail(e.target.value)} /></label>
          <div className="actions">
            <button type="button" className="btn" disabled={busy} onClick={() => void makeLink(true)}>{t('pay.email_it')}</button>
            <button type="button" className="btn primary" onClick={onDone}>{t('take.done')}</button>
          </div>
          <p className="sub" style={{ margin: 0 }}>{t('take.card_auto')}</p>
        </>
      )}
      <button type="button" className="linkish" onClick={() => setManual(true)}>{t('take.terminal_link')}</button>
    </div>
  );
}

// ---------- EFS / Comchek / T-Chek ----------
function FleetWay({ wo, onDone }: { wo: WorkOrder; onDone: () => void }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<(typeof FLEET_CHECKS)[number]>('comchek');
  const [code, setCode] = useState('');
  const [check, setCheck] = useState('');
  const [amount, setAmount] = useState(fromCents(wo.balance_cents));
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState<{ id: number; preview: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const pick = async (f: File | undefined) => {
    if (!f) return;
    setUploading(true);
    try {
      const r = await uploadFile(f, 'payment');
      setPhoto({ id: r.id, preview: fileUrl(r.id)! });
    } catch (e) { toastError(e); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const missing = { code: !code.trim(), check: !check.trim(), amount: cents(amount) <= 0, photo: !photo };
  const bad = (k: keyof typeof missing) => (tried && missing[k] ? ' invalid' : '');

  return (
    <form className="stack" noValidate onSubmit={async e => {
      e.preventDefault();
      setTried(true);
      if (Object.values(missing).some(Boolean)) { toast(t('take.fill_all'), true); return; }
      setBusy(true);
      try {
        await record(wo.id, { amount_cents: cents(amount), method: kind, express_code: code, check_number: check, photo_file_id: photo!.id, note: note || null });
        toast(t('common.saved')); onDone();
      } catch (err) { toastError(err); } finally { setBusy(false); }
    }}>
      <div className="seg take-seg" role="radiogroup" aria-label={t('take.check_type')}>
        {FLEET_CHECKS.map(k => <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>{t('pay.m.' + k)}</button>)}
      </div>
      <p className="sub" style={{ margin: 0 }}>{t('take.fleet_steps')}</p>
      <label className={'f' + bad('code')}>{t('take.express_code')} *
        <input value={code} inputMode="numeric" autoComplete="off" style={{ fontFamily: 'var(--mono)', letterSpacing: '.06em' }} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="0000 0000 00" /></label>
      <label className={'f' + bad('check')}>{t('take.check_number')} *
        <input value={check} inputMode="numeric" autoComplete="off" style={{ fontFamily: 'var(--mono)' }} onChange={e => setCheck(e.target.value)} /></label>
      <div className={bad('amount')}><AmountInput value={amount} onChange={setAmount} label={t('pay.amount')} /></div>
      <div className={'take-photo' + bad('photo')}>
        <span className="f-label">{t('take.check_photo')} *</span>
        {photo ? (
          <div className="take-photo-row">
            <a href={fileUrl(photo.id)!} target="_blank" rel="noreferrer"><img src={photo.preview} alt={t('take.check_photo')} /></a>
            <button type="button" className="btn sm" onClick={() => fileRef.current?.click()}>{t('take.retake')}</button>
          </div>
        ) : (
          <button type="button" className="btn take-cam" disabled={uploading} onClick={() => fileRef.current?.click()}>
            <Icon name="scan" size={22} />{uploading ? t('take.uploading') : t('take.take_photo')}
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" hidden onChange={e => void pick(e.target.files?.[0])} />
      </div>
      <label className="f">{t('pay.note')}<input value={note} onChange={e => setNote(e.target.value)} placeholder={t('take.fleet_note')} /></label>
      <button className="btn primary" disabled={busy || uploading}>{t('take.save')}</button>
    </form>
  );
}

// ---------- Cash ----------
function CashWay({ wo, onDone }: { wo: WorkOrder; onDone: () => void }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(fromCents(wo.balance_cents));
  const [got, setGot] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const change = got ? cents(got) - cents(amount) : null;
  return (
    <form className="stack" onSubmit={async e => {
      e.preventDefault();
      if (change != null && change < 0) { toast(t('err.cash_less_than_amount'), true); return; }
      setBusy(true);
      try { await record(wo.id, { amount_cents: cents(amount), method: 'cash', cash_received_cents: got ? cents(got) : null, note: note || null }); toast(t('common.saved')); onDone(); }
      catch (err) { toastError(err); } finally { setBusy(false); }
    }}>
      <AmountInput value={amount} onChange={setAmount} label={t('take.cash_amount')} />
      <label className="f">{t('take.cash_got')}<input type="number" inputMode="decimal" step="0.01" min="0" value={got} onChange={e => setGot(e.target.value)} /></label>
      {change != null && <div className={'take-change' + (change < 0 ? ' neg' : '')}>{change < 0 ? t('take.cash_short', { v: money(-change) }) : t('take.cash_change', { v: money(change) })}</div>}
      <label className="f">{t('pay.note')}<input value={note} onChange={e => setNote(e.target.value)} /></label>
      <button className="btn primary" disabled={busy}>{t('take.save')}</button>
    </form>
  );
}

// ---------- Paper check, ACH, other ----------
function OtherWay({ wo, onDone }: { wo: WorkOrder; onDone: () => void }) {
  const { t } = useTranslation();
  const [method, setMethod] = useState<'check' | 'ach' | 'other'>('check');
  const [amount, setAmount] = useState(fromCents(wo.balance_cents));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <form className="stack" onSubmit={async e => {
      e.preventDefault();
      setBusy(true);
      try { await record(wo.id, { amount_cents: cents(amount), method, note: note || null }); toast(t('common.saved')); onDone(); }
      catch (err) { toastError(err); } finally { setBusy(false); }
    }}>
      <label className="f">{t('pay.method')}<select value={method} onChange={e => setMethod(e.target.value as 'check')}>
        {(['check', 'ach', 'other'] as const).map(m => <option key={m} value={m}>{t('pay.m.' + m)}</option>)}</select></label>
      <AmountInput value={amount} onChange={setAmount} label={t('pay.amount')} />
      <label className="f">{t('pay.note')}<input value={note} onChange={e => setNote(e.target.value)} placeholder={t('pay.note_hint')} /></label>
      <button className="btn primary" disabled={busy}>{t('take.save')}</button>
    </form>
  );
}
